import React, { useState, useEffect } from 'react';
import AutoRenewOption from './AutoRenewOption';
import { BillingDetailsForm } from './BillingDetailsCard';
import { billingAPI } from '../../services/api';
import { usePaymentsPaused } from '../../hooks/usePaymentsPaused';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDateTime = (d) => new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

// Step 2 of checkout, shown in place of the plan cards once a plan is picked (PlansModal and
// RenewalRequired). The server works every number out (POST /billing/quote); this just displays it:
//   plan price -> cycle saving -> referral offer -> coupon -> subtotal -> GST -> total
// The coupon box only exists when a coupon could apply (not on a first paid plan) and only works for
// one-time payments — with auto-renew on it explains why instead.
const CheckoutSummary = ({ plan, billingCycle, cycleLabel, autoRenew, onAutoRenewChange, onBack, backLabel = '← Change plan', onPay, paying }) => {
  const [quote, setQuote] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCode, setAppliedCode] = useState(''); // the code last sent to the server
  // An affiliate's referral code — first paid plan only, works for both payment styles.
  const [referralInput, setReferralInput] = useState('');
  const [appliedReferral, setAppliedReferral] = useState('');
  // Autopay only: UPI or Card. A referral discount on autopay comes from a gateway Offer that belongs to
  // ONE payment method, so the customer chooses first and the Offer for that method is attached.
  const [payMethod, setPayMethod] = useState('upi');
  // Billing details (name, address, state) are required before paying: the server refuses payment without them,
  // so the form shows here first and the pay button stays off until they are saved.
  const [detailsSaved, setDetailsSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError('');
    billingAPI.getQuote({ planId: plan.id, billingCycle, couponCode: appliedCode || undefined, referralCode: appliedReferral || undefined, autoRenew, paymentMethod: payMethod })
      .then(({ data }) => { if (!cancelled) setQuote(data.quote); })
      .catch((err) => { if (!cancelled) setLoadError(err.response?.data?.message || 'Could not work out the price. Please try again.'); });
    return () => { cancelled = true; };
  }, [plan.id, billingCycle, autoRenew, appliedCode, appliedReferral, payMethod]);

  const L = quote?.lines;
  const couponState = quote?.coupon?.state;
  const couponApplied = couponState === 'applied';
  // A code that was refused shows its message under the box, and is cleared so the price is correct.
  const couponError = couponState === 'invalid' ? quote.coupon.message : '';

  const offerMethods = quote?.autopayOffer?.methods || { upi: false, card: false };
  const offerPercent = quote?.autopayOffer?.percent || 0;
  // Only for a referred customer with a first-purchase discount, and only on autopay.
  const showMethodPicker = !!autoRenew && offerPercent > 0;

  const referralApplied = quote?.referral?.state === 'applied';
  const referralError = quote?.referral?.state === 'invalid' ? quote.referral.message : '';
  const applyReferral = () => setAppliedReferral(referralInput.trim());
  const removeReferral = () => { setReferralInput(''); setAppliedReferral(''); };

  const applyCoupon = () => setAppliedCode(couponInput.trim());
  const removeCoupon = () => { setCouponInput(''); setAppliedCode(''); };

  // A same/lower plan that starts later can't be bought with autopay yet (see startInfo.autopayAllowed on the server).
  const waitsForOneTimePayment = !!quote && !!autoRenew && quote.startInfo?.mode === 'after_current' && !quote.startInfo?.autopayAllowed;

  // Payments paused by the server (the quote call is refused too, so this is mostly a clear message).
  const { paused, message: pausedMessage } = usePaymentsPaused();

  const needsBillingDetails = !!quote && !quote.billingDetailsComplete && !detailsSaved;

  const payLabel = paused ? 'Online payment paused'
    : !quote ? 'Please wait…'
    : needsBillingDetails ? 'Add billing details to continue'
    : waitsForOneTimePayment ? 'Switch auto-renew off to pay once'
      : autoRenew ? `Continue to auto-renew setup — ${money(L.total)}`
        : `Pay ${money(L.total)}`;

  return (
    <div style={s.wrap}>
      <button type="button" style={s.back} onClick={onBack} disabled={paying}>{backLabel}</button>
      <div style={s.title}>Order summary</div>

      {loadError ? (
        <div style={s.error}>{loadError}</div>
      ) : !quote ? (
        <div style={s.loading}>Calculating your price…</div>
      ) : (
        <>
          <div style={s.card}>
            <div style={s.row}>
              <span><strong>{quote.planName}</strong> — {cycleLabel}</span>
              <span>{money(L.planSaving > 0 ? L.listPrice : L.basePrice)}</span>
            </div>
            {L.planSaving > 0 && (
              <div style={{ ...s.row, ...s.good }}><span>{cycleLabel} plan saving</span><span>− {money(L.planSaving)}</span></div>
            )}
            {L.referralDiscount.amount > 0 && (
              <div style={{ ...s.row, ...s.good }}>
                <span>Referral offer ({Number(L.referralDiscount.percent)}% off your first purchase)</span>
                <span>− {money(L.referralDiscount.amount)}</span>
              </div>
            )}
            {couponApplied && (
              <div style={{ ...s.row, ...s.good }}>
                <span>Coupon <strong>{quote.coupon.code}</strong></span>
                <span>− {money(L.couponDiscount.amount)}</span>
              </div>
            )}
            <div style={{ ...s.row, ...s.divider }}><span>Subtotal (before GST)</span><span>{money(L.subtotal)}</span></div>
            <div style={s.row}><span>GST ({L.gst.percent}%)</span><span>{money(L.gst.amount)}</span></div>
            <div style={{ ...s.row, ...s.total }}><span>Total {autoRenew ? '(first charge)' : 'payable'}</span><span>{money(L.total)}</span></div>
          </div>

          {quote.startInfo && (
            <div style={{ ...s.startNote, ...(quote.startInfo.mode === 'after_current' ? s.startNoteWait : {}) }}>
              {quote.startInfo.mode === 'after_current' ? (
                <>
                  <strong>Starts {fmtDateTime(quote.startInfo.startsAt)}</strong>, when your current plan ends. You keep your current plan until
                  then{autoRenew && quote.startInfo.autopayAllowed ? ', and nothing is charged for this plan before that date' : ''}. It then runs until {fmtDateTime(quote.startInfo.endsAt)}.
                  {waitsForOneTimePayment && (
                    <div style={{ marginTop: 6, color: 'var(--warning)' }}>
                      Auto-renew isn't available for a plan that starts later yet. Switch auto-renew off below and pay once for this plan.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <strong>Starts right away</strong> and runs until {fmtDateTime(quote.startInfo.endsAt)}. Days left on your current plan are not added.
                </>
              )}
            </div>
          )}

          {quote.referralAvailable && (
            <div style={s.couponBox}>
              <div style={s.couponLabel}>Have a referral code?</div>
              {referralApplied ? (
                <div style={s.couponApplied}>
                  <span>
                    ✓ <strong>{quote.referral.code}</strong> applied
                    {quote.referral.percent > 0 ? ` — ${Number(quote.referral.percent)}% off your first purchase` : ''}
                  </span>
                  <button type="button" style={s.linkBtn} onClick={removeReferral} disabled={paying}>Remove</button>
                </div>
              ) : (
                <div style={s.couponRow}>
                  <input
                    style={s.couponInput}
                    value={referralInput}
                    onChange={(e) => setReferralInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyReferral(); } }}
                    placeholder="Enter referral code"
                    maxLength={32}
                    disabled={paying}
                  />
                  <button type="button" style={s.applyBtn} onClick={applyReferral} disabled={paying || !referralInput.trim()}>Apply</button>
                </div>
              )}
              {referralError && <div style={s.couponError}>{referralError}</div>}
            </div>
          )}

          {quote.couponAvailable && (
            <div style={s.couponBox}>
              <div style={s.couponLabel}>Have a coupon code?</div>
              {couponApplied ? (
                <div style={s.couponApplied}>
                  ✓ <strong>{quote.coupon.code}</strong> applied
                  <button type="button" style={s.linkBtn} onClick={removeCoupon} disabled={paying}>Remove</button>
                </div>
              ) : (
                <div style={s.couponRow}>
                  <input
                    style={s.couponInput}
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                    placeholder="Enter code"
                    maxLength={32}
                    disabled={paying}
                  />
                  <button type="button" style={s.applyBtn} onClick={applyCoupon} disabled={paying || !couponInput.trim()}>Apply</button>
                </div>
              )}
              {couponError && <div style={s.couponError}>{couponError}</div>}
              {couponState === 'autopay' && <div style={s.couponNote}>{quote.coupon.message}</div>}
              {autoRenew && couponState !== 'autopay' && !couponApplied && (
                <div style={s.couponNote}>Coupons apply to one-time payments only — switch auto-renew off below to use one.</div>
              )}
            </div>
          )}

          <AutoRenewOption autoRenew={autoRenew} onChange={onAutoRenewChange} discountPercent={0} cycleLabel={cycleLabel} />

          {showMethodPicker && (
            <div style={s.methodBox}>
              <div style={s.couponLabel}>How will you pay for auto-renew?</div>
              <div style={s.methodRow}>
                {['upi', 'card'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    style={{ ...s.methodBtn, ...(payMethod === m ? s.methodBtnOn : {}) }}
                    onClick={() => setPayMethod(m)}
                    disabled={paying}
                  >
                    {m === 'upi' ? 'UPI' : 'Card'}
                    {offerMethods[m] && <span style={s.methodTag}>{Number(offerPercent)}% off</span>}
                  </button>
                ))}
              </div>
              <div style={s.couponNote}>
                {offerMethods[payMethod]
                  ? `The ${Number(offerPercent)}% referral offer applies when you pay with ${payMethod === 'upi' ? 'UPI' : 'a card'}. You'll only see that method in the payment window.`
                  : `There's no referral offer for ${payMethod === 'upi' ? 'UPI' : 'card'} auto-renew, so this is the full price. ${(offerMethods.upi || offerMethods.card) ? `Choose ${offerMethods.upi ? 'UPI' : 'Card'} to get ${Number(offerPercent)}% off, or ` : ''}switch auto-renew off to pay once and get the discount on any method.`}
              </div>
            </div>
          )}

          {needsBillingDetails && (
            <div style={s.detailsBox}>
              <div style={s.couponLabel}>Billing details for your GST invoice</div>
              <div style={s.couponNote}>Required once, before your first payment. Add your GST number if you have one — your state is then taken from it.</div>
              <div style={{ marginTop: 10 }}>
                <BillingDetailsForm submitLabel="Save and continue" onSaved={() => setDetailsSaved(true)} />
              </div>
            </div>
          )}

          <button
            type="button"
            style={{ ...s.payBtn, opacity: paying || !quote || waitsForOneTimePayment || paused || needsBillingDetails ? 0.6 : 1 }}
            onClick={() => onPay(couponApplied && !autoRenew ? quote.coupon.code : undefined, showMethodPicker ? payMethod : undefined, referralApplied ? quote.referral.code : undefined)}
            disabled={paying || !quote || waitsForOneTimePayment || paused || needsBillingDetails}
          >
            {paying ? 'Starting checkout…' : `💳 ${payLabel}`}
          </button>
          {paused && <div style={s.fine}>{pausedMessage}</div>}
          <div style={s.fine}>Prices include {L.gst.percent}% GST in the total. Secure payment by Razorpay.</div>
        </>
      )}
    </div>
  );
};

const s = {
  wrap: { maxWidth: 560, margin: '0 auto 24px', textAlign: 'left' },
  back: { background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', padding: 0, marginBottom: 10 },
  title: { fontSize: 20, fontWeight: 800, marginBottom: 14, color: 'var(--text)' },
  loading: { padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 },
  error: { background: 'var(--danger-soft, rgba(239,68,68,0.12))', color: 'var(--danger, #ef4444)', borderRadius: 10, padding: '12px 14px', fontSize: 13 },
  card: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 14 },
  row: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: 14, color: 'var(--text)' },
  good: { color: 'var(--success)' },
  divider: { borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 10 },
  total: { fontSize: 17, fontWeight: 800, borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 10 },
  startNote: { background: 'var(--success-soft)', color: 'var(--text)', borderRadius: 10, padding: '10px 14px', fontSize: 13, lineHeight: 1.5, marginBottom: 14 },
  startNoteWait: { background: 'var(--surface-2)', border: '1px solid var(--border)' },
  couponBox: { marginBottom: 14 },
  detailsBox: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 14 },
  couponLabel: { fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text)' },
  couponRow: { display: 'flex', gap: 8 },
  couponInput: { flex: 1, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px', fontSize: 14, color: 'var(--text)', fontFamily: 'inherit', letterSpacing: 1 },
  applyBtn: { background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 8, padding: '0 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  couponApplied: { background: 'var(--success-soft)', color: 'var(--success)', borderRadius: 8, padding: '9px 12px', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  linkBtn: { background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  couponError: { color: 'var(--danger, #ef4444)', fontSize: 12, marginTop: 6 },
  couponNote: { color: 'var(--text-muted)', fontSize: 12, marginTop: 6, lineHeight: 1.45 },
  methodBox: { marginBottom: 14 },
  methodRow: { display: 'flex', gap: 8 },
  methodBtn: { flex: 1, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontWeight: 600, color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  methodBtnOn: { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent)' },
  methodTag: { background: 'var(--success-soft)', color: 'var(--success)', borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 700 },
  payBtn: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, var(--accent), var(--accent2))', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  fine: { textAlign: 'center', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 },
};

export default CheckoutSummary;
